<template>
  <el-container class="fixer-dashboard">
    <el-header class="header">
      <div class="header-left">
        <h2>小程序保修工作台</h2>
      </div>
      <div class="header-right">
        <el-dropdown @command="handleCommand">
          <span class="user-info">
            <el-avatar :size="32" :src="userStore.userInfo.avatar">
              {{ userStore.userInfo.username.charAt(0).toUpperCase() }}
            </el-avatar>
            <span class="username">{{ userStore.userInfo.username }}</span>
            <el-icon><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </el-header>

    <el-main class="main-content">
      <div class="stats-cards">
        <el-card class="stat-card">
          <div class="stat-number">{{ stats.total }}</div>
          <div class="stat-label">我的保修单</div>
        </el-card>
        <el-card class="stat-card">
          <div class="stat-number">{{ stats.pending }}</div>
          <div class="stat-label">未处理</div>
        </el-card>
        <el-card class="stat-card">
          <div class="stat-number">{{ stats.inProgress }}</div>
          <div class="stat-label">处理中</div>
        </el-card>
        <el-card class="stat-card">
          <div class="stat-number">{{ stats.closed }}</div>
          <div class="stat-label">已结单</div>
        </el-card>
      </div>

      <el-card class="repair-list-card">
        <template #header>
          <div class="card-header">
            <span>我的保修单</span>
            <el-button type="primary" :loading="loading" @click="fetchTickets">刷新</el-button>
          </div>
        </template>

        <div class="filter-row">
          <el-input v-model="searchKeyword" placeholder="搜索产品、客户、授权码/地址、联系人、问题描述" clearable class="filter-input" />
          <el-select v-model="statusFilter" clearable placeholder="状态" class="filter-select">
            <el-option label="未处理" value="PENDING" />
            <el-option label="处理中" value="IN_PROGRESS" />
            <el-option label="已结单" value="COMPLETED" />
          </el-select>
        </div>

        <el-table :data="filteredTickets" v-loading="loading" border stripe style="width: 100%; margin-top: 16px;">
          <el-table-column prop="id" label="ID" width="90" />
          <el-table-column prop="productName" label="产品名称" min-width="120" />
          <el-table-column prop="customerName" label="客户/门店" min-width="140" />
          <el-table-column prop="storeAuthCode" label="授权码/地址" min-width="140" />
          <el-table-column prop="contact" label="联系人" width="110" />
          <el-table-column prop="phone" label="联系电话" width="140">
            <template #default="{ row }">
              {{ row.phone || '—' }}
            </template>
          </el-table-column>
          <el-table-column prop="description" label="问题描述" min-width="220" show-overflow-tooltip>
            <template #default="{ row }">
              {{ row.description || '—' }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)">{{ getStatusText(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="报单时间" width="170">
            <template #default="{ row }">
              {{ formatDate(row.reportTime || row.createdAt) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click="openDetail(row)">详情</el-button>
              <el-button type="success" text @click="openStatusDialog(row)">更新状态</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </el-main>

    <el-drawer v-model="detailVisible" title="保修单详情" size="50%">
      <template v-if="detailTarget">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="保修单ID">{{ detailTarget.id }}</el-descriptions-item>
          <el-descriptions-item label="工单编号">{{ detailTarget.workOrderNo || '—' }}</el-descriptions-item>
          <el-descriptions-item label="当前状态">{{ getStatusText(detailTarget.status) }}</el-descriptions-item>
          <el-descriptions-item label="产品名称">{{ detailTarget.productName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="产品型号">{{ detailTarget.productModel || '—' }}</el-descriptions-item>
          <el-descriptions-item label="客户/门店">{{ detailTarget.customerName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="授权码/地址">{{ detailTarget.storeAuthCode || '—' }}</el-descriptions-item>
          <el-descriptions-item label="联系电话">{{ detailTarget.phone || '—' }}</el-descriptions-item>
          <el-descriptions-item label="提单人">{{ detailTarget.createdBy?.username || '—' }}</el-descriptions-item>
          <el-descriptions-item label="处理人">{{ detailTarget.processedBy?.username || detailTarget.assignee?.username || '—' }}</el-descriptions-item>
          <el-descriptions-item label="结单人">{{ detailTarget.closedBy?.username || '—' }}</el-descriptions-item>
          <el-descriptions-item label="处理方式">{{ detailTarget.handlingMethod || '—' }}</el-descriptions-item>
          <el-descriptions-item label="报单时间">{{ formatDate(detailTarget.reportTime || detailTarget.createdAt) }}</el-descriptions-item>
          <el-descriptions-item label="问题描述" :span="2">{{ detailTarget.description || '—' }}</el-descriptions-item>
          <el-descriptions-item label="服务内容" :span="2">{{ detailTarget.serviceContent || '—' }}</el-descriptions-item>
          <el-descriptions-item label="处理结果" :span="2">{{ detailTarget.processResult || detailTarget.serviceContent || '—' }}</el-descriptions-item>
          <el-descriptions-item label="结单备注" :span="2">{{ detailTarget.notes || '—' }}</el-descriptions-item>
        </el-descriptions>
      </template>
    </el-drawer>

    <el-dialog v-model="statusDialogVisible" title="更新保修单状态" width="520px">
      <el-form label-width="96px">
        <el-form-item label="保修单ID">
          <el-input :model-value="statusTarget?.id || ''" disabled />
        </el-form-item>
          <el-form-item label="新状态">
          <el-select v-model="statusForm.newStatus" style="width: 100%">
            <el-option label="处理中" value="IN_PROGRESS" />
            <el-option label="已结单" value="COMPLETED" />
          </el-select>
        </el-form-item>
        <el-form-item label="处理备注">
          <el-input v-model="statusForm.note" type="textarea" :rows="4" placeholder="请输入处理进展或备注" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="statusDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="updating" @click="submitStatusUpdate">确认更新</el-button>
      </template>
    </el-dialog>
  </el-container>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { RepairAPI } from '@/request/repair'
import { lowerText, trimText } from '@/utils/text'

export default {
  name: 'FixerDashboard',
  components: { ArrowDown },
  setup() {
    const router = useRouter()
    const userStore = useUserStore()
    const loading = ref(false)
    const updating = ref(false)
    const tickets = ref([])
    const searchKeyword = ref('')
    const statusFilter = ref('')
    const detailVisible = ref(false)
    const detailTarget = ref(null)
    const statusDialogVisible = ref(false)
    const statusTarget = ref(null)
    const statusForm = ref({
      newStatus: 'PROCESSING',
      note: ''
    })

    const fetchTickets = async () => {
      try {
        loading.value = true
        const list = await RepairAPI.getAssignedToMe()
        tickets.value = Array.isArray(list) ? list : []
      } catch (error) {
        ElMessage.error(error?.message || '获取保修单失败')
      } finally {
        loading.value = false
      }
    }

    const filteredTickets = computed(() => {
      const kw = lowerText(searchKeyword.value)
      return tickets.value.filter((item) => {
        const normalizedStatus = normalizeTicketStatus(item.status)
        const matchesStatus = !statusFilter.value || normalizedStatus === statusFilter.value
        if (!matchesStatus) return false
        if (!kw) return true
        const text = [
          item.productName,
          item.customerName,
          item.storeAuthCode,
          item.contact,
          item.phone,
          item.description
        ].filter(Boolean).join(' ').toLowerCase()
        return text.includes(kw)
      })
    })

    const stats = computed(() => ({
      total: tickets.value.length,
      pending: tickets.value.filter((item) => normalizeTicketStatus(item.status) === 'PENDING').length,
      inProgress: tickets.value.filter((item) => normalizeTicketStatus(item.status) === 'IN_PROGRESS').length,
      closed: tickets.value.filter((item) => normalizeTicketStatus(item.status) === 'COMPLETED').length
    }))

    const normalizeTicketStatus = (status) => ({
      PENDING: 'PENDING',
      IN_PROGRESS: 'IN_PROGRESS',
      PROCESSING: 'IN_PROGRESS',
      PENDING_VERIFY: 'IN_PROGRESS',
      COMPLETED: 'COMPLETED',
      CLOSED: 'COMPLETED',
      CANCELLED: 'COMPLETED'
    }[status] || status || '')

    const getStatusText = (status) => ({
      PENDING: '未处理',
      IN_PROGRESS: '处理中',
      PROCESSING: '处理中',
      PENDING_VERIFY: '处理中',
      COMPLETED: '已结单',
      CLOSED: '已结单'
    }[normalizeTicketStatus(status)] || '未知')

    const getStatusType = (status) => ({
      PENDING: 'warning',
      IN_PROGRESS: 'primary',
      COMPLETED: 'success'
    }[normalizeTicketStatus(status)] || 'info')

    const formatDate = (value) => {
      if (!value) return '—'
      try {
        return new Intl.DateTimeFormat('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).format(new Date(value))
      } catch {
        return String(value)
      }
    }

    const openDetail = async (row) => {
      detailVisible.value = true
      detailTarget.value = row
      try {
        detailTarget.value = await RepairAPI.getRepairDetail(row.id)
      } catch (error) {
        ElMessage.error(error?.message || '获取保修单详情失败')
      }
    }

    const openStatusDialog = (row) => {
      const normalizedStatus = normalizeTicketStatus(row.status)
      statusTarget.value = row
      statusForm.value = {
        newStatus: normalizedStatus === 'PENDING' ? 'IN_PROGRESS' : normalizedStatus,
        note: row.notes || ''
      }
      statusDialogVisible.value = true
    }

    const submitStatusUpdate = async () => {
      if (!statusTarget.value?.id) return
      try {
        updating.value = true
        await RepairAPI.updateRepairStatus(statusTarget.value.id, statusForm.value.newStatus)
        if (trimText(statusForm.value.note)) {
          await RepairAPI.addNotes(statusTarget.value.id, trimText(statusForm.value.note))
        }
        ElMessage.success('保修单状态已更新')
        statusDialogVisible.value = false
        await fetchTickets()
      } catch (error) {
        ElMessage.error(error?.message || '更新保修单状态失败')
      } finally {
        updating.value = false
      }
    }

    const handleCommand = (command) => {
      if (command === 'logout') logout()
    }

    const logout = async () => {
      try {
        await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        userStore.logout()
        router.push('/')
        ElMessage.success('已退出登录')
      } catch {}
    }

    onMounted(fetchTickets)

    return {
      userStore,
      loading,
      updating,
      searchKeyword,
      statusFilter,
      filteredTickets,
      stats,
      detailVisible,
      detailTarget,
      statusDialogVisible,
      statusTarget,
      statusForm,
      fetchTickets,
      getStatusText,
      getStatusType,
      formatDate,
      normalizeTicketStatus,
      openDetail,
      openStatusDialog,
      submitStatusUpdate,
      handleCommand
    }
  }
}
</script>

<style scoped>
.fixer-dashboard {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.header {
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.header-left h2 {
  margin: 0;
  color: #303133;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.username {
  color: #606266;
}

.main-content {
  padding: 20px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.stat-card {
  text-align: center;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
}

.stat-label {
  margin-top: 6px;
  color: #909399;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-input {
  width: 320px;
}

.filter-select {
  width: 160px;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

@media (max-width: 768px) {
  .stats-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
